import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Box, CircularProgress } from "@mui/material";
import { ApplicationHeader } from "./components/application/header";
import Footer from "./components/Footer";
import SignInForm from "./components/login/SignInForm.jsx";
import V1Routes from "./routes/V1Routes";
import V2Routes from "./routes/V2Routes";
import useApplicationData from "./hooks/useApplicationData";
import { useSelector } from "react-redux";
import { RefreshProvider, useRefresh } from "./contexts/RefreshContext";

function AppContent () {
  const {
    fetchApplicationData,
    fetchTotalApplicationsCount,
    fetchUniqueValues
  } = useApplicationData();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = useSelector(state => state.intakeData.currentPage);
  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );
  const [isLoading, setIsLoading] = useState(false);
  const { refreshTrigger, setRefreshState, resetTrigger } = useRefresh();
  const [lastRefreshTrigger, setLastRefreshTrigger] = useState(0);

  // Determine which routes version to use based on environment variable
  const appRoutes =
    process.env.REACT_APP_VERSION === "v1" ? <V1Routes /> : <V2Routes />;

  const pathParts = window.location.pathname.split("/");
  const dynamicAppId = pathParts[pathParts.length - 1] || "";
  const hasDynamicId =
    dynamicAppId && dynamicAppId.toLowerCase().includes("aiapp");

  const fetchData = useCallback(
    async (isRefreshCall = false) => {
      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");

      if (!accessToken || !refreshToken) {
        navigate("/");
        return;
      }

      setIsLoading(true);
      if (isRefreshCall) {
        setRefreshState(true);
      }

      try {
        await fetchTotalApplicationsCount(accessToken, refreshToken);
        await fetchUniqueValues(accessToken, refreshToken);
        await fetchApplicationData(accessToken, refreshToken);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
        if (isRefreshCall) {
          setRefreshState(false);
        }
      }
    },
    [
      navigate,
      setRefreshState,
      fetchTotalApplicationsCount,
      fetchUniqueValues,
      fetchApplicationData
    ]
  );

  // Regular data fetching for route changes
  useEffect(() => {
    // Only fetch data when not on the sign in page
    if (
      location.pathname === "/app/details" &&
      currentPage === 1 &&
      !selectedApplicationId
    ) {
      fetchData(false); // Regular fetch, not a refresh call
    }

    if (hasDynamicId && !selectedApplicationId) {
      fetchData(false); // Regular fetch, not a refresh call
    }
  }, [
    location.pathname,
    currentPage,
    selectedApplicationId,
    hasDynamicId,
    fetchData
  ]);

  // Separate effect for handling refresh triggers
  useEffect(() => {
    if (refreshTrigger > 0 && refreshTrigger !== lastRefreshTrigger) {
      setLastRefreshTrigger(refreshTrigger);

      // Use a separate function to avoid dependency issues
      const performRefresh = async () => {
        const accessToken = localStorage.getItem("access_token");
        const refreshToken = localStorage.getItem("refresh_token");

        if (!accessToken || !refreshToken) {
          navigate("/");
          return;
        }

        setIsLoading(true);
        setRefreshState(true);

        try {
          await fetchTotalApplicationsCount(accessToken, refreshToken);
          await fetchUniqueValues(accessToken, refreshToken);
          await fetchApplicationData(accessToken, refreshToken);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setIsLoading(false);
          setRefreshState(false);
          resetTrigger(); // Reset the trigger after processing
        }
      };

      performRefresh();
    }
  }, [
    refreshTrigger,
    lastRefreshTrigger,
    navigate,
    setRefreshState,
    resetTrigger,
    fetchTotalApplicationsCount,
    fetchUniqueValues,
    fetchApplicationData
  ]);

  const hideHeaderFooter =
    location.pathname === "/app/extraction-viewer" ||
    location.pathname === "/app/interpretation-guide";

  return (
    <div>
      {isLoading && location.pathname !== "/" && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CircularProgress size={60} sx={{ color: "#ea580c" }} />
        </Box>
      )}

      <div className="min-h-screen overflow-hidden bg-gray-100">
        {!hideHeaderFooter && <ApplicationHeader />}
        <Routes>
          <Route path="/" element={<SignInForm />} />
          {/* Conditionally load version-specific routes */}
          <Route path="/*" element={appRoutes} />
        </Routes>
      </div>
      {!hideHeaderFooter && <Footer />}
    </div>
  );
}

function App () {
  return (
    <RefreshProvider>
      <AppContent />
    </RefreshProvider>
  );
}

export default App;
